import { useState, useEffect } from 'react';
import { Layout } from '@frontend/components/Layout';
import { ModelMetadata } from '@pipelet/common';
import { ModelStatusIndicator } from '@frontend/components/ModelStatusIndicator';
import { useModelStatus } from '@frontend/hooks/useModelStatus';
import {
  Box,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import { useNavigate } from 'react-router-dom';

export default function Models(): JSX.Element {
  const navigate = useNavigate();
  const [models, setModels] = useState<ModelMetadata[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { getStatus } = useModelStatus(true);

  const handleChatWithModel = (modelName: string) => {
    navigate(`/chat?model=${encodeURIComponent(modelName)}`);
  };

  useEffect(() => {
    const loadModels = async () => {
      try {
        const response = await fetch(`/api/v1/models`);
        if (!response.ok) throw new Error(await response.text());
        const data = (await response.json()) as { data: ModelMetadata[] };
        setModels(data.data);
      } catch (error) {
        console.error('Failed to load models:', error);
        setError(error instanceof Error ? error.message : 'Failed to load models');
      } finally {
        setLoading(false);
      }
    };
    loadModels();
  }, []);

  return (
    <Layout title="Models">
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Name</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Context Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  // Loading skeleton
                  [...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Skeleton width="16px" />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="60%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="70%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="50%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="40%" />
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  ))
                ) : models.length > 0 ? (
                  models.map((model) => (
                    <TableRow key={model.name} hover>
                      <TableCell>
                        <ModelStatusIndicator status={getStatus(model.name)} />
                      </TableCell>
                      <TableCell>{model.name}</TableCell>
                      <TableCell>{model.displayName}</TableCell>
                      <TableCell>{model.provider}</TableCell>
                      <TableCell>{model.contextSize?.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Chat with this model">
                          <IconButton size="small" color="primary" onClick={() => handleChatWithModel(model.name)}>
                            <ChatIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">No models available</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Layout>
  );
}
